import React from 'react';
import LegalPage, { HR_CONTACT, List, Section, Term } from './LegalPage';

const PrivacyPolicy = () => (
  <LegalPage title="Privacy Policy" lastUpdated="25 August 2026">
    <Section heading="Who this applies to">
      <p>
        The ByteCorp HR Portal is an internal system used by ByteCorp employees, contractors
        and interns. This policy explains what personal data the Portal holds about you, why
        it holds it, and who it is shared with. It covers the Portal only - it is not a
        general statement about ByteCorp's website or its customers.
      </p>
      <p>
        ByteCorp is the controller of this data. The Portal is not open to the public and
        holds no data about anyone outside the organisation.
      </p>
    </Section>

    <Section heading="What the Portal holds about you">
      <List>
        <Term label="Identity and contact.">
          Name, work email address, personal email address, phone number, CNIC, date of
          birth, gender, and home address.
        </Term>
        <Term label="Employment.">
          Position, department, reporting manager, hire date, employment type and status,
          and last working date.
        </Term>
        <Term label="Emergency contact.">
          The name, phone number and relationship of the person you nominate.
        </Term>
        <Term label="Compensation.">
          Salary and salary history, currency, bank and payment method details, transaction
          fees, and tax status.
        </Term>
        <Term label="Performance.">
          Appraisal records, ratings, raise decisions, and reviewer and employee comments.
        </Term>
        <Term label="Leave.">
          Leave requests and the reasons given, approvals and rejections, and leave balances.
        </Term>
        <Term label="Attendance.">
          Your daily sign-in time, whether you worked from the office or from home, lateness,
          and out-of-office periods. These are derived from your own posts in the company
          <span className="whitespace-nowrap"> #availability </span>
          Slack channel, together with your Slack user ID.
        </Term>
        <Term label="Documents.">
          Files uploaded to your employee record by you or by HR, such as contracts and
          certificates.
        </Term>
        <Term label="Account and security.">
          Username, a one-way hash of your password (never the password itself), the email
          address your Google sign-in resolves to, sign-in timestamps, and audit records of
          changes made to your account.
        </Term>
      </List>
    </Section>

    <Section heading="Where it comes from">
      <List>
        <li>Directly from you, through the onboarding form and your profile page.</li>
        <li>From HR and your manager, in the course of administering your employment.</li>
        <li>
          From your company Google Workspace account, if you sign in with Google - see the
          section below.
        </li>
        <li>
          From the company <span className="whitespace-nowrap">#availability</span> Slack
          channel, for attendance.
        </li>
      </List>
    </Section>

    <Section heading="Signing in with Google">
      <p>
        If you use "Sign in with Google", Google confirms to the Portal which email address
        you signed in with and whether that address is verified. That is all the Portal
        receives and all it uses: the address is matched against an existing Portal account
        and nothing more.
      </p>
      <List>
        <li>Your Google password is never sent to, seen by, or stored by the Portal.</li>
        <li>
          The Portal requests no access to Gmail, Drive, Calendar, Contacts or any other
          Google service, and cannot read them.
        </li>
        <li>
          Only company addresses are accepted, and only if an HR record already exists for
          them. Signing in with Google never creates an account.
        </li>
      </List>
    </Section>

    <Section heading="Why the Portal holds it">
      <List>
        <li>To run payroll and meet statutory tax and record-keeping obligations.</li>
        <li>To administer leave, attendance and performance review.</li>
        <li>To decide what each person is allowed to see, and to enforce that.</li>
        <li>
          To keep a security and compliance audit trail of who changed what, which the
          Portal is required to retain.
        </li>
      </List>
      <p>
        The Portal is not used for advertising or profiling, and your data is never sold or
        rented.
      </p>
    </Section>

    <Section heading="Who it is shared with">
      <p>
        Inside ByteCorp, access is limited by role: HR and administrators can see records
        across the organisation, managers can see their own department, and everyone else
        can see only their own record. Salary and document access is narrower still.
      </p>
      <p>Outside ByteCorp, the Portal relies on these service providers:</p>
      <List>
        <Term label="Google.">
          Sign-in, if you use it. Google also processes the
          <span className="whitespace-nowrap"> #availability </span>
          messages used for attendance through its Gemini API, in order to interpret what
          each message says about that day.
        </Term>
        <Term label="Slack.">
          The source of attendance data, being the channel you post to.
        </Term>
        <Term label="SendGrid.">
          Sends Portal emails, such as your initial invitation.
        </Term>
      </List>
      <p>
        Data is also disclosed where the law requires it, or to ByteCorp's professional
        advisers where necessary.
      </p>
    </Section>

    <Section heading="How long it is kept">
      <p>
        Employment records are kept for as long as you work at ByteCorp and afterwards for
        as long as tax, employment and audit law requires. Security audit records - who
        created, changed, approved or deactivated an account, a leave request or a report -
        are append-only and are retained as compliance evidence even after the account
        itself is removed.
      </p>
    </Section>

    <Section heading="How it is protected">
      <List>
        <li>Traffic to the Portal is encrypted in transit.</li>
        <li>
          Passwords are stored only as bcrypt hashes and cannot be recovered or read by
          anyone, including administrators.
        </li>
        <li>
          Your session is held in a cookie that JavaScript cannot read and that is not sent
          on cross-site requests, and it can be revoked by the server at any time.
        </li>
        <li>
          Every screen and endpoint is gated by role-based permissions, and account changes
          are written to an append-only audit log.
        </li>
      </List>
    </Section>

    <Section heading="Cookies">
      <p>
        The Portal sets one cookie, named <code className="rounded bg-gray-100 px-1 py-0.5 text-xs">token</code>,
        which holds your session and is strictly necessary for signing in. There are no
        analytics, advertising or third-party tracking cookies. If you sign in with Google,
        Google sets its own cookies on its own domain under its own privacy policy.
      </p>
    </Section>

    <Section heading="Your choices">
      <p>
        You can view and correct your own contact details from your profile page. For
        anything else - a correction to your employment or salary record, a copy of what is
        held about you, or a question about this policy - contact HR at{' '}
        <a className="font-medium text-primary-700 hover:text-primary-800" href={`mailto:${HR_CONTACT}`}>
          {HR_CONTACT}
        </a>.
      </p>
      <p>
        Because the Portal exists to administer your employment, most of this data cannot be
        deleted on request while you are employed and while retention obligations apply.
      </p>
    </Section>

    <Section heading="Changes">
      <p>
        If this policy changes materially, the Portal will say so on this page and update the
        date above. Continuing to use the Portal after a change means the updated policy
        applies.
      </p>
    </Section>
  </LegalPage>
);

export default PrivacyPolicy;
